import React, { useEffect, useState, useRef } from "react";
import "./HomePage.css";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Footer1 from "../components/Footer1";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import MobileNav from "../components/MobileNav";
import { motion, AnimatePresence } from "framer-motion";
import { useDispatch } from "react-redux";
import { setCategory } from "../redux/filterSlice";

// --- Helper Component for FAQ Accordion ---
const FAQItem = ({ question, answer }) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <div className="faq-item" onClick={() => setIsOpen(!isOpen)}>
            <div className="faq-question">
                <h5>{question}</h5>
                <span className={`faq-icon ${isOpen ? "open" : ""}`}>{isOpen ? "−" : "+"}</span>
            </div>
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                        className="faq-answer"
                    >
                        <p>{answer}</p>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

const HomePage = () => {
    const [paintings, setPaintings] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const dispatch = useDispatch();

    // ✅ TOGGLE THIS BOOLEAN: true = Image Poster, false = Video Loop
    const useImageBanner = true;

    useEffect(() => {
        window.scrollTo(0, 0);

        // 1. Lock screen scroll & set loading to true for the cinematic entrance
        setLoading(true);
        document.body.style.overflow = "hidden";

        // 2. Fetch the data in the background
        const fetchPaintings = async () => {
            try {
                const res = await axios.get("http://localhost:8080/api/paintings/all");
                setPaintings(res.data);
            } catch (err) {
                console.error("Error fetching paintings:", err);
            }
        };
        fetchPaintings();

        // 3. Keep the luxury loader on screen for 2.2 seconds while assets fetch
        const timer = setTimeout(() => {
            setLoading(false);
            document.body.style.overflow = "auto"; // Unlock scroll
        }, 2200);

        return () => {
            clearTimeout(timer);
            document.body.style.overflow = "auto";
        };
    }, []);

    // --- LUXURY ANIMATION VARIANTS ---
    const fadeUpSlow = {
        hidden: { opacity: 0, y: 40 },
        visible: { opacity: 1, y: 0, transition: { duration: 1, ease: [0.16, 1, 0.3, 1] } }
    };

    const staggerContainer = {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { staggerChildren: 0.25, delayChildren: 0.4 } }
    };

    const imageReveal = {
        hidden: { opacity: 0, filter: "blur(10px)" },
        visible: { opacity: 1, filter: "blur(0px)", transition: { duration: 1.5, ease: "easeOut", delay: 0.2 } }
    };

    return (
        <>
            {/* --- AESTHETIC BACKGROUND LOADER --- */}
            <AnimatePresence>
                {loading && (
                    <motion.div
                        key="elite-loader"
                        initial={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.8, ease: "easeInOut" }}
                        style={{
                            position: "fixed",
                            inset: 0,
                            zIndex: 99999,
                            backgroundColor: "#0a0a0a",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center"
                        }}
                    >
                        <motion.span
                            animate={{ opacity: [0.3, 1, 0.3] }}
                            transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}
                            style={{
                                fontFamily: "'Playfair Display', serif",
                                fontSize: "1.1rem",
                                textTransform: "uppercase",
                                letterSpacing: "8px",
                                color: "#c4a36e",
                                marginBottom: "25px"
                            }}
                        >
                            Entering the Atelier
                        </motion.span>
                        <div style={{ width: "100px", height: "1px", backgroundColor: "rgba(255,255,255,0.08)", overflow: "hidden", position: "relative" }}>
                            <motion.div
                                initial={{ x: "-100%" }}
                                animate={{ x: "100%" }}
                                transition={{ repeat: Infinity, duration: 1.2, ease: "easeInOut" }}
                                style={{
                                    width: "100%",
                                    height: "100%",
                                    backgroundColor: "#c4a36e",
                                    position: "absolute"
                                }}
                            />
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <MobileNav />

            {/* HERO SECTION */}
            <section className={`hero luxury-hero ${useImageBanner ? 'has-bg-image' : 'has-bg-video'}`}
                style={useImageBanner ? { backgroundImage: "url('https://res.cloudinary.com/wbwvb4sk/image/upload/v1786597523/main-bg.png')" } : {}}>
                {!useImageBanner && (
                    <div className="hero-video-wrapper">
                        <video autoPlay loop muted playsInline className="hero-background-video">
                            {/* <source src="https://res.cloudinary.com/digze3wuc/video/upload/v1784709995/IMG_2320_dhq73k.mp4" type="video/mp4" /> */}
                        </video>
                    </div>
                )}
                <div className="hero-overlay"></div>
                <motion.div className="hero-content" initial="hidden" animate={!loading ? "visible" : "hidden"} variants={staggerContainer}>
                    <motion.span variants={fadeUpSlow} className="hero-eyebrow">The Atelier of Anjaan Vivek</motion.span>
                    <motion.h1 variants={fadeUpSlow} className="hero-title">A Legacy of Canvas & Color</motion.h1>
                    <motion.div variants={fadeUpSlow} className="hero-action">
                        <button onClick={() => navigate("/allPaintings/All Paintings")} className="btn-luxury-primary">
                            Explore Collection
                        </button>
                        <button onClick={() => navigate("/about")} className="btn-signature">
                            Know the Artist
                        </button>
                    </motion.div>
                </motion.div>
            </section>

            {/* SPLIT CHOICE SECTION */}
            <section className="split-choice-section">
                <div className="split-panel signature-panel" 
                style={{ backgroundImage: "url('https://res.cloudinary.com/wbwvb4sk/image/upload/v1786597536/signaturebgimg_grusyi.jpg')" }}   
                onClick={() => {
                    dispatch(setCategory("SIGNATURE"));
                    navigate("/allPaintings/All Paintings")
                }}>
                    <div className="panel-overlay"></div>
                    <div className="panel-content">
                        <span className="panel-subtitle">Original Masterpieces</span>
                        <h2>Signature Collection</h2>
                        <p>Exclusive pictorial biographies & originals by Anjaan Vivek.</p>
                        <button className="btn-split btn-signature">Acquire Originals</button>
                    </div>
                </div>

                <div className="split-panel prints-panel" 
                style={{ backgroundImage: "url('https://res.cloudinary.com/wbwvb4sk/image/upload/v1786597547/uttejana_3.jpg')" }}   
                onClick={() => {
                    dispatch(setCategory("PRINTS"));
                    navigate("/allPrints")
                }}>
                    <div className="panel-overlay"></div>
                    <div className="panel-content">
                        <span className="panel-subtitle">Fine Art Reproductions</span>
                        <h2>Limited Edition Prints</h2>
                        <p>Acquire certified archival prints of Anjaan Vivek’s masterworks</p>
                        <button className="btn-split btn-prints">Acquire Prints</button>
                    </div>
                </div>
            </section>

            <main className="homepage-container">
                {/* CURATED CATEGORIES */}
                <motion.section className="category-section" initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }} variants={staggerContainer}>
                    <div className="category-header">
                        <motion.span variants={fadeUpSlow} className="sub-title">Curated Collections</motion.span>
                        <motion.h4 variants={fadeUpSlow}>Explore by Category</motion.h4>
                    </div>

                    <motion.div className="category-creative-grid" variants={staggerContainer}>
                        <motion.div variants={fadeUpSlow} className="art-card card-innovations" 
                        onClick={() => navigate("/allpaintings/Mythological")}>
                            <div className="art-card-bg"
                            style={{ backgroundImage: "url('https://res.cloudinary.com/wbwvb4sk/image/upload/v1786597515/Bal_Hanuman_2.png')" }}
                            ></div>
                            <div className="art-card-content">
                                <h3>Mythological</h3>
                                <span className="explore-link">Discover Details</span>
                            </div>
                        </motion.div>
                        <motion.div variants={fadeUpSlow} className="art-card card-mythological center-card" onClick={() => navigate("/allpaintings/Pictorial Biography")}>
                            <div className="art-card-bg"
                            style={{ backgroundImage: "url('https://res.cloudinary.com/wbwvb4sk/image/upload/v1786597538/sonu_sood_2.jpg')" }}
                            ></div>
                            <div className="art-card-content">
                                <h3>Pictorial Biography</h3>
                                <span className="explore-link">Discover Details</span>
                            </div>
                        </motion.div>
                        <motion.div variants={fadeUpSlow} className="art-card card-positive" onClick={() => navigate("/allpaintings/Positive Energy")}>
                            <div className="art-card-bg"
                            style={{ backgroundImage: "url('https://res.cloudinary.com/wbwvb4sk/image/upload/v1786597544/udaan_red_2.png')" }}
                            ></div>
                            <div className="art-card-content">
                                <h3>Positive Energy</h3>
                                <span className="explore-link">Discover Details</span>
                            </div>
                        </motion.div>
                    </motion.div>
                    <motion.div className="btn-center-wrapper" variants={fadeUpSlow}>
                        <button className="btn-luxury-outline" onClick={() => navigate("/allPaintings/All Paintings")}>View Complete Gallery</button>
                    </motion.div>
                </motion.section>

                {/* BESPOKE COMMISSION SECTION */}
                <motion.section className="bespoke-bio-section" initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }}>
                    <div className="bespoke-bio-container">
                        <motion.div className="bespoke-image-wrapper" variants={imageReveal}>
                            <div className="bespoke-image-frame">
                                <img src="https://res.cloudinary.com/wbwvb4sk/image/upload/v1786597518/elon-frame.jpg" alt="Bespoke Commission" />
                            </div>
                        </motion.div>

                        <motion.div className="bespoke-content" variants={staggerContainer}>
                            <motion.span variants={fadeUpSlow} className="sub-title-gold">The Ultimate Visual Heirloom</motion.span>
                            <motion.h2 variants={fadeUpSlow} className="bespoke-title">Your Legacy in a Single Frame</motion.h2>
                            <motion.div variants={fadeUpSlow} className="bespoke-divider"></motion.div>
                            <motion.p variants={fadeUpSlow} className="bespoke-text">
                                The <strong>Single-Frame Pictorial Biography</strong> by Anjaan Vivek is an unprecedented leap in contemporary fine art. Moving beyond traditional portraiture, it captures the complete essence of a lifetime on a continuous canvas, where true greatness is defined by your actions, triumphs, and <em>karma</em>.
                            </motion.p>
                            <motion.p variants={fadeUpSlow} className="bespoke-text">
                                Commissioned exclusively for visionaries and distinguished families, this bespoke masterpiece serves as a profound visual time capsule—transforming your extraordinary journey into a historic heirloom for future generations.
                            </motion.p>
                            <motion.div variants={fadeUpSlow} className="bespoke-actions">
                                <button className="btn-luxury-primary" onClick={() => navigate("/customBiography")}>Request a Private Commission</button>
                                <a href="https://wa.me/919300847841?text=Dear Anjaan Vivek Studio, I wish to inquire about commissioning a private Pictorial Biography." target="_blank" rel="noreferrer" className="btn-luxury-text">Inquire via Concierge</a>
                            </motion.div>
                        </motion.div>
                    </div>
                </motion.section>

                {/* THE MAGNUM OPUS - THE WORLD FRAME SERIES */}
                <motion.section className="world-frame-section" initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }}>
                    <div className="world-frame-container">
                        <motion.div className="world-frame-content" variants={staggerContainer}>
                            <motion.span variants={fadeUpSlow} className="world-frame-eyebrow">The Flagship Project</motion.span>
                            <motion.h2 variants={fadeUpSlow} className="world-frame-title">The World Frame Series</motion.h2>
                            <motion.p variants={fadeUpSlow} className="world-frame-text">
                                A groundbreaking, globally significant endeavor. The World Frame Series isolates the defining titans of our era and meticulously records their chronological journeys on massive, unbroken canvases.
                            </motion.p>
                            <motion.p variants={fadeUpSlow} className="world-frame-text">
                                Each frame acts as a generational time capsule—capturing not just the face of a visionary, but the friction, the genius, and the silent moments that forged their empire.
                            </motion.p>
                            <motion.div variants={fadeUpSlow} className="world-frame-actions">
                                <button className="btn-world-frame" onClick={() => navigate("/worldFrameSeries")}>Explore the World Frames</button>
                            </motion.div>
                        </motion.div>

                        <motion.div className="world-frame-visuals" variants={imageReveal}>
                            <div className="world-frame-image-wrapper">
                                <img src="https://res.cloudinary.com/wbwvb4sk/image/upload/v1786597528/narendra_modi.jpg" alt="The World Frame Series - Narendra Modi" className="world-frame-img" />
                                <div className="world-frame-badge">
                                    <span>Vol. 1</span>
                                    <strong>Elon Musk</strong>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </motion.section>

                {/* THE ERAWAT - FEATURED ACQUISITION */}
                <motion.section className="light-series-section" initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }}>
                    <div className="light-series-wrapper">
                        <motion.div className="light-image-side" variants={imageReveal}>
                            <img src="https://res.cloudinary.com/wbwvb4sk/image/upload/v1786597518/Erawat.jpg" alt="The Erawat" className="feature-painting" />
                        </motion.div>

                        <motion.div className="light-content-side" variants={staggerContainer}>
                            <motion.span variants={fadeUpSlow} className="sub-title-gold">Featured Masterwork</motion.span>
                            <motion.h2 variants={fadeUpSlow} className="light-series-title">The Erawat</motion.h2>
                            <motion.p variants={fadeUpSlow} className="light-series-desc">
                                An embodiment of majestic sovereignty and divine wisdom. This singular masterwork captures the regal aura of the celestial elephant through intricate textures and profound depth—a commanding centerpiece for any curated estate.
                            </motion.p>
                            <motion.button variants={fadeUpSlow} className="btn-luxury-outline" onClick={() => navigate("/originalDetails/8")}>
                                Inspect Masterwork
                            </motion.button>
                        </motion.div>
                    </div>
                </motion.section>

                {/* CURATED SET (Bundle) */}
                <motion.section className="series-bundle-section" initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }}>
                    <div className="bundle-container">
                        <motion.div className="bundle-info" variants={staggerContainer}>
                            <motion.span variants={fadeUpSlow} className="sub-title-gold">Curated Collection</motion.span>
                            <motion.h3 variants={fadeUpSlow} className="bundle-title">Udaan – The Flight</motion.h3>
                            <motion.p variants={fadeUpSlow} className="bundle-desc">
                                'Udaan' is not merely a series of paintings; it is a visual philosophy that celebrates the extraordinary journey of human life. Through a harmonious interplay of colors, movement, and emotion, Anjaan Vivek explores the universal path of aspiration, perseverance, transformation, and achievement.
                                <br /><br />
                                Every individual dreams of soaring beyond limitations. Yet no flight begins at the summit. It begins with courage, progresses through perseverance, and ultimately arrives at fulfillment.
                                <br /><br />
                                Each painting represents a distinct phase of this journey, where color becomes the language of emotion and every brushstroke reflects the spirit of human resilience.
                                <br /><br />
                                {/* <em>Pricing and provenance details are available upon private request.</em> */}
                            </motion.p>
                            <motion.button variants={fadeUpSlow} className="btn-luxury-primary" onClick={() => navigate("series/Udaan")}>
                                View Anthology Details
                            </motion.button>
                        </motion.div>

                        <motion.div className="bundle-visuals" variants={staggerContainer}>
                            <div className="bundle-grid luxury-three-grid">
                                <motion.img variants={imageReveal} src="https://res.cloudinary.com/wbwvb4sk/image/upload/v1786597543/Udaan_red_1.jpg" alt="Udaan - The Beginning" />
                                <motion.img variants={imageReveal} src="https://res.cloudinary.com/wbwvb4sk/image/upload/v1786597542/Udaan_blue_1_2.jpg" alt="Udaan - The Progress" />
                                <motion.img variants={imageReveal} src="https://res.cloudinary.com/wbwvb4sk/image/upload/v1786597544/Udaan_yellow_1.jpg" alt="Udaan - The Achievement" />
                            </div>
                        </motion.div>
                    </div>
                </motion.section>

                {/* SUBTLE NEWS TICKER */}
                <section className="news-section luxury-ticker">
                    <div className="scrolling-text-container">
                        <div className="scrolling-text">
                            <h5>Creating global resonance: World's First <b>Pictorial Biography in a Single Frame</b>, introduced by Anjaan Vivek.</h5>
                            <span className="ticker-divider">•</span>
                            <h5>Acclaimed by leading voices in fine art and culture.</h5>
                            <span className="ticker-divider">•</span>
                        </div>
                    </div>
                </section>

                {/* PRESS LOGOS */}
                <motion.section className="press-logos luxury-press" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}>
                    {[
                        "https://res.cloudinary.com/wbwvb4sk/image/upload/v1786597516/dainik-bhaskar-8517_icirsg.png",
                        "https://res.cloudinary.com/wbwvb4sk/image/upload/v1786597529/Peoples-Samachar-logo_p2ofhj.jpg",
                        "https://res.cloudinary.com/wbwvb4sk/image/upload/v1786597528/patrika_oyvvmr.jpg",
                        "https://res.cloudinary.com/wbwvb4sk/image/upload/v1786597541/the-free-press-journal-logo-png_seeklogo-469740_knvcv7.png",
                        "https://res.cloudinary.com/wbwvb4sk/image/upload/v1786597519/haribhumi__shcwaq.jpg"
                    ].map((src, index) => (
                        <motion.img key={index} src={src} alt="press" variants={fadeUpSlow} />
                    ))}
                </motion.section>

                {/* ARTIST SECTION - UPDATED */}
                <motion.section className="artist-section luxury-artist" initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.4 }}>
                    <motion.div className="artist-photo" 
                        style={{ backgroundImage: "url('https://res.cloudinary.com/wbwvb4sk/image/upload/v1786597515/artist.jpg')" }}
                    variants={imageReveal}>
                        <div className="artist-overlay">
                            <h3>Anjaan Vivek</h3>
                        </div>
                    </motion.div>
                    <motion.div className="artist-info" variants={staggerContainer}>
                        <motion.span variants={fadeUpSlow} className="sub-title-gold">About the Artist</motion.span>
                        <motion.h2 variants={fadeUpSlow} className="section-title">Anjaan Vivek</motion.h2>
                        <motion.p variants={fadeUpSlow} className="artist-bio-text">
                            Anjaan Vivek is a pioneering contemporary artist and the renowned innovator of the <strong>"Pictorial Biography in a Single Frame"</strong>. Redefining modern storytelling, he is the creator of the world's first painting series based entirely on this groundbreaking concept: <strong>The World Frame Series</strong>.
                        </motion.p>
                        <motion.p variants={fadeUpSlow} className="artist-bio-text" style={{ marginTop: "15px" }}>
                            His visionary approach meticulously documents the chronological life journeys, struggles, and defining triumphs of global titans onto monumental, unbroken canvases, establishing a new frontier in biographical art.
                        </motion.p>
                        <motion.button variants={fadeUpSlow} className="btn-luxury-outline" onClick={() => {
                            navigate("/about")
                        }}>Explore Full Biography</motion.button>
                    </motion.div>
                </motion.section>

                {/* CERTIFICATE OF AUTHENTICITY */}
                <motion.section className="certificate-section" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}>
                    <div className="certificate-container">
                        <motion.div className="cert-content" variants={fadeUpSlow}>
                            <span className="sub-title-gold">Provenance Guaranteed</span>
                            <h2 className="section-title">The Seal of Originality</h2>
                            <p className="cert-text">
                                True luxury demands absolute certainty. Every masterwork acquired is accompanied by a physically signed, tamper-proof <strong>Certificate of Authenticity</strong>.
                            </p>
                            <p className="cert-text cert-subtext">
                                This document serves as the absolute guarantee of provenance, securing the investment value of your artwork for generations to come.
                            </p>
                            <div className="signature-block">
                                <span>Authenticated By</span>
                                <img src="https://upload.wikimedia.org/wikipedia/commons/e/e4/Signature_sample.svg" alt="Signature" className="artist-signature" />
                            </div>
                        </motion.div>

                        <motion.div className="cert-image-wrapper" variants={imageReveal}>
                            <img src="https://res.cloudinary.com/wbwvb4sk/image/upload/v1786597519/Gemini_Generated_Image_okulnnokulnnokul_z0msro_1.png" alt="Certificate of Authenticity" className="cert-mockup" />
                            <div className="cert-badge-luxury">
                                <i className="fa-solid fa-award"></i> Studio Certified
                            </div>
                        </motion.div>
                    </div>
                </motion.section>

                {/* PRIVATE INQUIRIES (FAQ) */}
                <section className="faq-section">
                    <div className="section-header-center">
                        <span className="sub-title-gold">Concierge Services</span>
                        <h4 className="section-title">Acquisition Inquiries</h4>
                    </div>
                    <div className="faq-container">
                        <FAQItem question="Are the artworks framed prior to delivery?" answer="We offer bespoke framing consultations for all local acquisitions. International deliveries are shipped securely in archival tubes to ensure pristine condition upon arrival." />
                        <FAQItem question="Do you provide global white-glove shipping?" answer="Yes. We partner with elite fine art couriers to deliver our masterpieces securely to over 50 countries, fully insured from studio to your residence." />
                        <FAQItem question="How is the authenticity of the artwork verified?" answer="Each original creation is cataloged in the artist's registry and is accompanied by a physically signed Certificate of Authenticity detailing its provenance." />
                    </div>
                </section>

                <Footer1 />
            </main>
        </>
    );
};

export default HomePage;